// Google Analytics Tracking Utilities
// Measurement ID: G-TFJ3FKB2FH

/**
 * Initialize analytics tracking
 */
export function initAnalytics() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initScrollTracking();
            trackEngagementTime();
        });
    } else {
        initScrollTracking();
        trackEngagementTime();
    }
}

/**
 * Track page views
 */
export function trackPageView(pageTitle, pagePath) {
    if (window.gtag) {
        window.gtag('event', 'page_view', {
            page_title: pageTitle,
            page_path: pagePath
        });
    }
}

/**
 * Track custom events
 */
export function trackEvent(eventName, eventParams = {}) {
    if (window.gtag) {
        window.gtag('event', eventName, eventParams);
    }
}

/**
 * Track waitlist form interactions
 */
export function trackWaitlistFormStart() {
    trackEvent('form_start', {
        form_name: 'waitlist_form',
        form_destination: 'waitlist_signup'
    });
}

export function trackWaitlistFormSuccess(email) {
    trackEvent('waitlist_signup', {
        event_category: 'engagement',
        event_label: 'Waitlist Form Submission',
        value: 1
    });

    // Track conversion
    trackEvent('conversion', {
        send_to: 'G-TFJ3FKB2FH',
        value: 1.0,
        currency: 'USD',
        transaction_id: Date.now().toString()
    });
}

export function trackWaitlistFormError(errorMessage) {
    trackEvent('form_error', {
        form_name: 'waitlist_form',
        error_message: errorMessage
    });
}

/**
 * Track CTA button clicks
 */
export function trackCTAClick(ctaName, ctaLocation) {
    trackEvent('cta_click', {
        cta_name: ctaName,
        cta_location: ctaLocation
    });
}

/**
 * Track scroll depth
 */
function initScrollTracking() {
    let scrollDepths = [25, 50, 75, 100];
    let triggeredDepths = new Set();

    window.addEventListener('scroll', () => {
        const scrollPercentage = Math.round(
            (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        );

        scrollDepths.forEach(depth => {
            if (scrollPercentage >= depth && !triggeredDepths.has(depth)) {
                triggeredDepths.add(depth);
                trackEvent('scroll_depth', {
                    percent_scrolled: depth
                });
            }
        });
    });
}

/**
 * Track section visibility
 */
export function trackSectionView(sectionName) {
    trackEvent('section_view', {
        section_name: sectionName
    });
}

/**
 * Track outbound links
 */
export function trackOutboundLink(url, linkText) {
    trackEvent('click', {
        event_category: 'outbound',
        event_label: linkText,
        transport_type: 'beacon',
        url: url
    });
}

/**
 * Track product interest
 */
export function trackProductInterest(productName) {
    trackEvent('view_item', {
        items: [{
            item_name: productName,
            item_category: 'skincare'
        }]
    });
}

/**
 * GA4 E-commerce: view_item (ViewContent)
 * Also fires Facebook Pixel ViewContent and Pinterest viewitem
 */
export function trackViewItem(product) {
    const price = parseFloat(product.price || 0);

    trackEvent('view_item', {
        currency: 'AUD',
        value: price,
        items: [{
            item_id: product.id,
            item_name: product.name,
            item_category: 'skincare',
            price: price,
        }]
    });

    // Facebook Pixel: ViewContent
    window.fbq?.('track', 'ViewContent', {
        content_name: product.name,
        content_ids: [String(product.id)],
        content_type: 'product',
        value: price,
        currency: 'AUD',
    });

    // Pinterest: viewitem
    window.pintrk?.('track', 'viewitem', {
        product_id: product.id,
        product_name: product.name,
        product_category: 'skincare',
        product_price: price,
        currency: 'AUD',
    });
}

/**
 * GA4 E-commerce: add_to_cart (AddToCart)
 * Also fires Facebook Pixel AddToCart and Pinterest addtocart
 */
export function trackAddToCart(product, quantity) {
    const price = parseFloat(product.price || 0);
    const value = price * quantity;

    trackEvent('add_to_cart', {
        currency: 'AUD',
        value: value,
        items: [{
            item_id: product.id,
            item_name: product.name,
            item_category: 'skincare',
            price: price,
            quantity: quantity,
        }]
    });

    // Facebook Pixel: AddToCart
    window.fbq?.('track', 'AddToCart', {
        content_name: product.name,
        content_ids: [String(product.id)],
        content_type: 'product',
        value: value,
        currency: 'AUD',
    });

    // Pinterest: addtocart
    window.pintrk?.('track', 'addtocart', {
        product_id: product.id,
        product_name: product.name,
        product_quantity: quantity,
        product_price: price,
        order_quantity: quantity,
        value: value,
        currency: 'AUD',
    });
}

/**
 * GA4 E-commerce: begin_checkout (InitiateCheckout)
 * Also fires Facebook Pixel InitiateCheckout and Pinterest checkout
 */
export function trackBeginCheckout(cart) {
    const items = (cart.items || []).map(item => ({
        item_id: item.product?.id,
        item_name: item.product?.name,
        item_category: 'skincare',
        price: parseFloat(item.product?.price || 0),
        quantity: item.quantity,
    }));
    const value = parseFloat(cart.subtotal || 0);
    const numItems = (cart.items || []).length;

    trackEvent('begin_checkout', {
        currency: 'AUD',
        value: value,
        items: items,
    });

    // Facebook Pixel: InitiateCheckout
    window.fbq?.('track', 'InitiateCheckout', {
        content_ids: items.map(i => String(i.item_id)),
        content_type: 'product',
        value: value,
        currency: 'AUD',
        num_items: numItems,
    });

    // Pinterest: checkout
    window.pintrk?.('track', 'checkout', {
        value: value,
        currency: 'AUD',
        order_quantity: numItems,
        line_items: items.map(i => ({
            product_id: i.item_id,
            product_quantity: i.quantity,
            product_price: i.price,
        })),
    });
}

/**
 * GA4 E-commerce: purchase (Purchase)
 * Also fires Facebook Pixel Purchase and Pinterest checkout (purchase)
 */
export function trackPurchase(order) {
    const items = (order.items || []).map(item => ({
        item_id: item.product_id,
        item_name: item.product_name,
        item_category: 'skincare',
        price: parseFloat(item.unit_price || item.total_price || 0),
        quantity: item.quantity,
    }));
    const value = parseFloat(order.total || 0);
    const currency = order.currency || 'AUD';

    trackEvent('purchase', {
        transaction_id: order.order_number,
        currency: currency,
        value: value,
        shipping: parseFloat(order.shipping_cost || 0),
        items: items,
    });

    // Facebook Pixel: Purchase
    window.fbq?.('track', 'Purchase', {
        content_ids: items.map(i => String(i.item_id)),
        content_type: 'product',
        content_category: 'skincare',
        value: value,
        currency: currency,
        num_items: items.length,
    });

    // Pinterest: checkout (purchase event)
    window.pintrk?.('track', 'checkout', {
        value: value,
        currency: currency,
        order_id: order.order_number,
        order_quantity: items.length,
        line_items: items.map(i => ({
            product_id: i.item_id,
            product_name: i.item_name,
            product_category: 'skincare',
            product_quantity: i.quantity,
            product_price: i.price,
        })),
    });
}

/**
 * Track user engagement time
 */
function trackEngagementTime() {
    let startTime = Date.now();

    window.addEventListener('beforeunload', () => {
        const engagementTime = Math.round((Date.now() - startTime) / 1000);

        if (window.gtag) {
            window.gtag('event', 'user_engagement', {
                engagement_time_msec: engagementTime * 1000
            });
        }
    });
}
